import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { getSubscriptionStatus } from '../../requests/subscription';
import { getProgressColor } from './subscriptionUtils';
import { SUBSCRIPTION_UPGRADE_ROUTE } from '../../routes.config';

const styles = {
  card: {
    marginBottom: 16,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  chip: {
    textTransform: 'uppercase',
  },
};

function SubscriptionStatusWidgetBase({ classes }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getSubscriptionStatus()
      .then(setStatus)
      .catch((err) => console.error('[SubscriptionStatusWidget] failed to load status:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgradeClick = () => navigate({ to: SUBSCRIPTION_UPGRADE_ROUTE });

  if (loading) return <CircularProgress size={24} />;
  if (!status) return null;

  const { tier, status: subStatus, currentPeriodEnd, usage, entitlements } = status;

  // Hide widget when tier is unknown or missing — subscription data is incomplete
  if (!tier || tier.toLowerCase() === 'unknown') return null;

  // Use trialEnd for trialing subscriptions, currentPeriodEnd for active ones
  const trialEnd = status.trialEnd;
  const expiryDate = subStatus === 'trialing' ? (trialEnd || currentPeriodEnd) : currentPeriodEnd;
  const daysRemaining = subStatus === 'trialing' && expiryDate
    ? Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  const docLimit = entitlements?.maxDocumentsPerMonth ?? 0;
  const docUsed = usage?.documentsProcessed ?? 0;
  const docPercentage = docLimit > 0 ? Math.min((docUsed / docLimit) * 100, 100) : 0;

  const progressColor = getProgressColor(docPercentage);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.header}>
          <Box>
            <Typography variant="h6">{(tier ?? 'Unknown').toUpperCase()} Plan</Typography>
            <Chip
              label={subStatus}
              size="small"
              color={subStatus === 'active' ? 'success' : subStatus === 'trialing' ? 'info' : 'warning'}
              className={classes.chip}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleUpgradeClick}
          >
            Upgrade
          </Button>
        </Box>

        {subStatus === 'trialing' && daysRemaining > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left in trial</strong>
            {' — '}
            Your subscription activates automatically when the trial ends.
          </Alert>
        )}

        {docLimit > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Box className={classes.progressLabel}>
              <Typography variant="caption">Documents this month</Typography>
              <Typography variant="caption">
                {docUsed}
                {' '}
                /
                {' '}
                {docLimit}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={docPercentage} color={progressColor} />
          </Box>
        ) : (
          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
            {`Documents: ${docUsed} (Unlimited)`}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          {'Published Playbooks: '}
          {entitlements?.maxPlaybooks === -1
            ? `${usage?.publishedPlaybooks ?? 0} (Unlimited)`
            : `${usage?.publishedPlaybooks ?? 0} / ${entitlements?.maxPlaybooks ?? 0}`}
        </Typography>
      </CardContent>
    </Card>
  );
}

SubscriptionStatusWidgetBase.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(SubscriptionStatusWidgetBase);
